import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { listAgents, AgentSummary } from '../../api/agents';

interface Phone {
  phone_number_id: string;
  twilio_phone_number: string;
  country_code: string;
  region?: string;
  is_active: boolean;
  agent_id: string;
  purchased_at?: string;
}

function StatusPill({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(107,114,128,0.2)', color: '#9ca3af' }}>
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
      Inactive
    </span>
  );
}

export const PhoneNumbers: React.FC = () => {
  const [phones, setPhones] = useState<Phone[]>([]);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [agentId, setAgentId] = useState<string>('');
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [loadingPhones, setLoadingPhones] = useState(true);

  const loadAgents = async () => {
    try {
      const res = await listAgents(1, 100);
      setAgents(res.agents || []);
    } catch (e) {
      console.error('Failed to load agents', e);
    }
  };

  const loadPhones = async () => {
    setLoadingPhones(true);
    try {
      const params: any = {};
      if (agentId) params.agent_id = agentId;
      const res = await api.get('/api/v1/phones', { params });
      setPhones(res.data.phones || []);
    } catch (e) {
      console.error('Failed to load phones', e);
    } finally {
      setLoadingPhones(false);
    }
  };

  useEffect(() => { loadAgents(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPhones(); }, [agentId]);

  const search = async () => {
    setSearching(true);
    setSearchError(null);
    try {
      const params: any = { country };
      if (areaCode) params.areaCode = Number(areaCode);
      const res = await api.get('/api/v1/phones/search', { params });
      setSearchResults(res.data.results || []);
      setShowSearch(true);
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Search failed. Check Twilio credentials.';
      setSearchError(msg);
    } finally {
      setSearching(false);
    }
  };

  const purchase = async (phoneNumber: string) => {
    if (!agentId) {
      setSearchError('Select an agent before purchasing a number.');
      return;
    }
    setPurchasing(phoneNumber);
    try {
      await api.post('/api/v1/phones/purchase', { agent_id: agentId, phoneNumber });
      setShowSearch(false);
      setSearchResults([]);
      await loadPhones();
    } catch (e: any) {
      setSearchError(e?.response?.data?.error?.message || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const release = async (phone_number_id: string, number: string) => {
    if (!window.confirm(`Release ${number}? This cannot be undone.`)) return;
    setReleasing(phone_number_id);
    try {
      await api.post(`/api/v1/phones/${phone_number_id}/release`);
      await loadPhones();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || 'Release failed');
    } finally {
      setReleasing(null);
    }
  };

  const inputCls = `w-full rounded-lg px-3 py-2.5 text-sm text-white outline-none transition-colors`;
  const inputStyle = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' };

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Phone Numbers</h1>
        <p className="text-gray-400 text-sm mt-1">Manage Twilio numbers assigned to your AI agents.</p>
      </div>

      {/* Search Panel */}
      <div className="rounded-xl p-4 md:p-5 mb-6" style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 className="text-sm font-semibold text-white mb-3">Search & Purchase</h2>

        {/* Agent selector */}
        <div className="mb-3">
          <label className="block text-xs text-gray-400 mb-1">Assign to agent</label>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className={inputCls}
            style={inputStyle}
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
            ))}
          </select>
        </div>

        {/* Country + Area Code + Search button */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Country</label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              maxLength={2}
              placeholder="US"
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Area code (optional)</label>
            <input
              type="text"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 415"
              maxLength={3}
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={search}
              disabled={searching}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-50"
              style={{ background: 'rgba(99,102,241,0.8)' }}
            >
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
        </div>

        {searchError && (
          <p className="mt-3 text-xs text-red-400">⚠️ {searchError}</p>
        )}
      </div>

      {/* Search Results Modal/Panel */}
      {showSearch && (
        <div className="rounded-xl p-4 md:p-5 mb-6" style={{ background: '#111118', border: '1px solid rgba(99,102,241,0.3)' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-white">
              Available numbers {searchResults.length > 0 && `(${searchResults.length})`}
            </h2>
            <button onClick={() => setShowSearch(false)} className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
          </div>

          {!agentId && (
            <div className="mb-3 px-3 py-2 rounded-lg text-xs text-yellow-400" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
              ⚠️ Select an agent above before purchasing
            </div>
          )}

          {searchResults.length === 0 ? (
            <p className="text-sm text-gray-400">No numbers found. Try a different country or area code.</p>
          ) : (
            <div className="space-y-2">
              {searchResults.map((n: any) => (
                <div
                  key={n.phoneNumber}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                  <div>
                    <div className="text-sm font-mono text-white">{n.phoneNumber}</div>
                    {n.friendlyName && <div className="text-xs text-gray-400">{n.friendlyName}</div>}
                  </div>
                  <button
                    onClick={() => purchase(n.phoneNumber)}
                    disabled={!agentId || purchasing === n.phoneNumber}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity disabled:opacity-40"
                    style={{ background: 'rgba(16,185,129,0.7)' }}
                  >
                    {purchasing === n.phoneNumber ? 'Buying…' : 'Purchase'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filter by agent */}
      {agents.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-gray-400">Filter:</span>
          <button
            onClick={() => setAgentId('')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${agentId === '' ? 'text-white' : 'text-gray-400'}`}
            style={{ background: agentId === '' ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)' }}
          >
            All
          </button>
          {agents.map((a) => (
            <button
              key={a.agent_id}
              onClick={() => setAgentId(a.agent_id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${agentId === a.agent_id ? 'text-white' : 'text-gray-400'}`}
              style={{ background: agentId === a.agent_id ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)' }}
            >
              {a.agent_name}
            </button>
          ))}
        </div>
      )}

      {/* Phone numbers list */}
      {loadingPhones ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : phones.length === 0 ? (
        <div className="rounded-xl py-16 text-center" style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-4xl mb-3">📞</div>
          <p className="text-white font-medium mb-1">No phone numbers yet</p>
          <p className="text-gray-400 text-sm">Search and purchase a number above to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {phones.map((p) => {
            const agentName = agents.find((a) => a.agent_id === p.agent_id)?.agent_name;
            return (
              <div
                key={p.phone_number_id}
                className="rounded-xl px-4 py-4"
                style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                {/* Top row: number + status */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="text-base font-mono font-semibold text-white">{p.twilio_phone_number}</div>
                    {agentName && (
                      <div className="text-xs text-gray-400 mt-0.5">Agent: {agentName}</div>
                    )}
                  </div>
                  <StatusPill active={p.is_active} />
                </div>

                {/* Bottom row: region + date + release */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {p.region && <span>{p.region}</span>}
                    {p.country_code && <span>{p.country_code}</span>}
                    {p.purchased_at && (
                      <span>Purchased {new Date(p.purchased_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  {p.is_active && (
                    <button
                      onClick={() => release(p.phone_number_id, p.twilio_phone_number)}
                      disabled={releasing === p.phone_number_id}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                    >
                      {releasing === p.phone_number_id ? 'Releasing…' : 'Release'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
